import React, { useState, useEffect } from "react";
import PageTitle from "../../Components/PageTitle";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Breadcrumbs,
  Link as MUILink,
  Chip,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { message, Modal } from "antd";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "../../axiosInstance";

const Reports = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const [reportsData, setReportsData] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get all reports by user
  const getReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/report/all`);
      if (response?.data?.success) {
        const reports = response?.data?.data || [];
        setReportsData(reports);
        setFilteredReports(reports);
      } else {
        setReportsData([]);
        setFilteredReports([]);
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Error fetching reports");
      setReportsData([]);
      setFilteredReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReports();
  }, []);

  // delete Report by Id
  const deleteReport = async (id) => {
    if (!id) return;

    Modal.confirm({
      title: "Are you sure you want to delete this Report?",
      content:
        "This action will permanently delete the report. Are you sure you want to continue?",
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      onOk: async () => {
        try {
          const { data } = await axios.delete(
            `/api/v1/report/${id}`,
            // {
            //   data: { id },
            // },
          );
          if (data.success === true) {
            message.success(data.message);
            getReports();
          } else {
            message.error(data.message);
          }
        } catch (error) {
          message.error(
            error?.response?.data?.message || "Error deleting report",
          );
        }
      },
    });
  };

  // Search functionality
  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    if (!query.trim()) {
      setFilteredReports(reportsData);
      return;
    }

    const filtered = reportsData.filter((report) => {
      if (!report) return false;
      return (
        (report.examName && report.examName.toLowerCase().includes(query)) ||
        (report.studentName &&
          report.studentName.toLowerCase().includes(query)) ||
        (report.student?.email &&
          report.student.email.toLowerCase().includes(query))
      );
    });
    setFilteredReports(filtered);
  };

  // Share functionality
  const handleShare = (report) => {
    if (!report) return;
    navigate("/admin/share/report", { state: { report } });
  };

  // View functionality
  const handleView = (report) => {
    if (!report) return;
    setSelectedReport(report);
    setViewModalOpen(true);
  };

  // Download single report as PDF
  const downloadSingleReport = (report) => {
    if (!report) return;

    Modal.confirm({
      title: "Download Report",
      content: "Do you want to download this student's report as a PDF?",
      okText: "Yes, Download",
      cancelText: "No, Cancel",
      onOk: () => {
        const doc = new jsPDF();

        doc.setFontSize(20);
        doc.setTextColor(33, 33, 33);
        doc.text("Student Exam Report", 105, 20, { align: "center" });

        doc.setFontSize(12);
        doc.setTextColor(100, 100, 100);
        doc.text(
          `Generated on: ${moment().format("DD/MM/YYYY HH:mm:ss")}`,
          105,
          30,
          { align: "center" },
        );

        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text("Student Information", 16, 50);

        doc.setFontSize(11);
        doc.setTextColor(80, 80, 80);
        doc.text(`Student Name: ${report.studentName || "N/A"}`, 16, 60);
        doc.text(`Student Email: ${report.student?.email || "N/A"}`, 16, 68);
        doc.text(
          `Student Class: ${report.studentClass || report.student?.class || "N/A"}`,
          16,
          76,
        );

        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text("Exam Information", 16, 92);

        doc.setFontSize(11);
        doc.setTextColor(80, 80, 80);
        doc.text(`Exam Name: ${report.examName || "N/A"}`, 16, 102);
        doc.text(`Subject: ${report.examSubject || "N/A"}`, 16, 110);
        doc.text(
          `Date: ${moment(report.createdAt).format("DD/MM/YYYY HH:mm:ss")}`,
          16,
          118,
        );

        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text("Results Summary", 16, 134);

        const summaryData = [
          ["Total Marks", report.totalMarks || 0],
          ["Passing Marks", report.passingMarks || 0],
          ["Obtained Marks", (report.obtainedMarks || 0).toFixed(2)],
          ["Percentage", `${report.percentage || 0}%`],
          ["Verdict", report.verdict || "N/A"],
          ["Total Questions", report.totalQuestions || 0],
          ["Attempted Questions", report.attemptedQuestions || 0],
          ["Correct Answers", report.correctAnswers || 0],
          ["Wrong Answers", report.wrongAnswers || 0],
          ["Skipped Answers", report.skippedAnswers || 0],
          ["Negative Marks", (report.negativeMarks || 0).toFixed(2)],
          ["Time Taken", `${report.timeTaken || 0} minutes`],
        ];

        autoTable(doc, {
          startY: 142,
          head: [["Metric", "Value"]],
          body: summaryData,
          theme: "striped",
          headStyles: { fillColor: [25, 118, 210], textColor: 255 },
          margin: { left: 16, right: 16 },
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text("Answers Details", 16, finalY);

        const answersData =
          report.answers?.map((answer, idx) => [
            idx + 1,
            answer.questionId?.slice(-8) || "N/A",
            answer.selectedOptions?.join(", ") || "N/A",
            answer.isCorrect ? "✓ Correct" : "✗ Incorrect",
            (answer.marksAwarded || 0).toFixed(2),
          ]) || [];

        if (answersData.length > 0) {
          autoTable(doc, {
            startY: finalY + 5,
            head: [["#", "Question ID", "Selected Options", "Status", "Marks"]],
            body: answersData,
            theme: "striped",
            headStyles: { fillColor: [25, 118, 210], textColor: 255 },
            margin: { left: 16, right: 16 },
          });
        }

        doc.save(
          `${report.studentName || "Student"}_${report.examName || "Exam"}_Report.pdf`,
        );
      },
    });
  };

  // Export all filtered reports to PDF
  const exportAllToPDF = () => {
    if (!filteredReports.length) {
      message.warning("No reports to export");
      return;
    }

    Modal.confirm({
      title: "Download All Reports",
      content: "Do you want to download all filtered reports as a PDF?",
      okText: "Yes, Download",
      cancelText: "No, Cancel",
      onOk: () => {
        const doc = new jsPDF();
        const columns = [
          "#",
          "Student Name",
          "Student Email",
          "Exam Name",
          "Subject",
          "Total Marks",
          "Obtained Marks",
          "Percentage",
          "Verdict",
          "Report Date",
        ];

        const rows = filteredReports.map((report, index) => [
          index + 1,
          report?.studentName || "N/A",
          report?.student?.email || "N/A",
          report?.examName || "N/A",
          report?.examSubject || "N/A",
          report?.totalMarks || 0,
          (report?.obtainedMarks || 0).toFixed(2),
          `${report?.percentage || 0}%`,
          report?.verdict || "N/A",
          report?.createdAt
            ? moment(report.createdAt).format("DD/MM/YYYY")
            : "N/A",
        ]);

        doc.setFontSize(18);
        doc.text("All Student Reports", 105, 16, { align: "center" });
        doc.setFontSize(10);
        doc.text(
          `Generated on: ${moment().format("DD/MM/YYYY HH:mm:ss")}`,
          105,
          24,
          { align: "center" },
        );
        doc.text(`Total Reports: ${filteredReports.length}`, 105, 32, {
          align: "center",
        });

        autoTable(doc, {
          head: [columns],
          body: rows,
          startY: 40,
          theme: "striped",
          headStyles: { fillColor: [25, 118, 210], textColor: 255 },
          styles: { fontSize: 8, cellPadding: 2 },
        });

        doc.save("All_Student_Reports.pdf");
      },
    });
  };

  // Define columns - Using renderCell instead of valueGetter to avoid the undefined error
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => {
        const row = params.row;
        if (!row) return null;

        return (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
          >
            <IconButton
              aria-label="view"
              onClick={() => handleView(row)}
              sx={{ color: "#4caf50" }}
              size="small"
            >
              <VisibilityIcon />
            </IconButton>
            <IconButton
              aria-label="download"
              onClick={() => downloadSingleReport(row)}
              sx={{ color: "#1976d2" }}
              size="small"
            >
              <DownloadIcon />
            </IconButton>
            {/* <IconButton
              aria-label="share"
              onClick={() => handleShare(row)}
              sx={{ color: "#ff9800" }}
              size="small"
            >
              <ShareIcon />
            </IconButton> */}
            <IconButton
              aria-label="delete"
              onClick={() => deleteReport(row._id)}
              sx={{ color: "red" }}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "studentName",
      headerName: "Student Name",
      width: 150,
      renderCell: (params) => {
        return params.row?.studentName || "N/A";
      },
    },
    {
      field: "studentEmail",
      headerName: "Student Email",
      width: 200,
      renderCell: (params) => {
        return params.row?.student?.email || "N/A";
      },
    },
    {
      field: "examName",
      headerName: "Exam Name",
      width: 180,
      renderCell: (params) => {
        return params.row?.examName || "N/A";
      },
    },
    {
      field: "examSubject",
      headerName: "Subject",
      width: 130,
      renderCell: (params) => {
        return params.row?.examSubject || "N/A";
      },
    },
    {
      field: "totalMarks",
      headerName: "Total Marks",
      width: 100,
      renderCell: (params) => {
        return params.row?.totalMarks || 0;
      },
    },
    {
      field: "obtainedMarks",
      headerName: "Obtained Marks",
      width: 120,
      renderCell: (params) => {
        const marks = params.row?.obtainedMarks;
        return marks ? marks.toFixed(2) : "0.00";
      },
    },
    {
      field: "percentage",
      headerName: "Percentage",
      width: 100,
      renderCell: (params) => {
        const percentage = params.row?.percentage;
        return `${percentage || 0}%`;
      },
    },
    {
      field: "verdict",
      headerName: "Verdict",
      width: 100,
      renderCell: (params) => {
        const verdict = params.row?.verdict;
        return (
          <Chip
            label={verdict || "N/A"}
            color={
              verdict === "Pass"
                ? "success"
                : verdict === "Fail"
                  ? "error"
                  : "default"
            }
            size="small"
          />
        );
      },
    },
    {
      field: "reportDate",
      headerName: "Report Date",
      width: 130,
      renderCell: (params) => {
        return params.row?.createdAt
          ? moment(params.row.createdAt).format("DD/MM/YYYY")
          : "N/A";
      },
    },
  ];


  // View Modal Component
  const ViewReportModal = () => {
    if (!selectedReport) return null;

    return (
      <Modal
        title="Student Report Details"
        open={viewModalOpen}
        onCancel={() => setViewModalOpen(false)}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        <Box sx={{ maxHeight: "80vh", overflowY: "auto", p: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#1976d2", mb: 2 }}
          >
            Student Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                Student Name
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedReport.studentName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                Student Email
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedReport.student?.email || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                Student Class
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedReport.studentClass ||
                  selectedReport.student?.class ||
                  "N/A"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#1976d2", mb: 2 }}
          >
            Exam Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Exam Name
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedReport.examName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Subject
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedReport.examSubject || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Report Date
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedReport.createdAt
                  ? moment(selectedReport.createdAt).format(
                      "DD/MM/YYYY HH:mm:ss",
                    )
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                Time Taken
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedReport.timeTaken || 0} minutes
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#1976d2", mb: 2 }}
          >
            Results Summary
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f5f5f5" }}>
                <Typography variant="body2" color="textSecondary">
                  Total Marks
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {selectedReport.totalMarks || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f5f5f5" }}>
                <Typography variant="body2" color="textSecondary">
                  Passing Marks
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {selectedReport.passingMarks || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#e3f2fd" }}>
                <Typography variant="body2" color="textSecondary">
                  Obtained Marks
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {(selectedReport.obtainedMarks || 0).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor:
                    selectedReport.verdict === "Pass" ? "#c8e6c9" : "#ffcdd2",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Verdict
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={selectedReport.verdict === "Pass" ? "green" : "red"}
                >
                  {selectedReport.verdict || "N/A"}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f5f5f5" }}>
                <Typography variant="body2" color="textSecondary">
                  Percentage
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {selectedReport.percentage || 0}%
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#e8f5e9" }}>
                <Typography variant="body2" color="textSecondary">
                  Correct Answers
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="green">
                  {selectedReport.correctAnswers || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#ffebee" }}>
                <Typography variant="body2" color="textSecondary">
                  Wrong Answers
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="red">
                  {selectedReport.wrongAnswers || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#fff3e0" }}>
                <Typography variant="body2" color="textSecondary">
                  Skipped Answers
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {selectedReport.skippedAnswers || 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#1976d2", mb: 2 }}
          >
            Answers Details
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#1976d2" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#1976d2",
                      color: "white",
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#1976d2",
                      color: "white",
                    }}
                  >
                    Question ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#1976d2",
                      color: "white",
                    }}
                  >
                    Selected Options
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#1976d2",
                      color: "white",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#1976d2",
                      color: "white",
                    }}
                  >
                    Marks
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedReport.answers && selectedReport.answers.length > 0 ? (
                  selectedReport.answers.map((answer, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {answer.questionId?.slice(-8) || "N/A"}
                      </TableCell>
                      <TableCell>
                        {answer.selectedOptions?.join(", ") || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={answer.isCorrect ? "Correct" : "Incorrect"}
                          color={answer.isCorrect ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          color: answer.marksAwarded > 0 ? "green" : "red",
                        }}
                      >
                        {(answer.marksAwarded || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No answers data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={() => downloadSingleReport(selectedReport)}
            >
              Download Report
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  return (
    <div>
      <PageTitle
        title="MCQ Exam Reports - Institute Dashboard & Performance Insights"
        description="View detailed MCQ exam reports in your institute dashboard. Track student performance, analyze test results, and download reports for effective exam management."
        keywords="institute MCQ exam reports, student performance tracking, MCQ test results, online exam analytics, institute test series, exam performance insights, MCQ report download"
      />
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        All Exam Reports
      </Typography>

      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <MUILink
          component={Link}
          to="/admin/exams"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Exams
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>Reports</Typography>
      </Breadcrumbs>

      <Box
        sx={{ mb: 2, display: "flex", justifyContent: "space-between", gap: 2 }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Student Name, Email or Exam Name"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <IconButton>
                <SearchIcon sx={{ color: "#1976d2" }} />
              </IconButton>
            ),
          }}
          sx={{
            mb: 3,
            bgcolor: "white",
            borderRadius: "20px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "none" },
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={exportAllToPDF}
          startIcon={<DownloadIcon />}
          sx={{
            borderRadius: "5px",
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            letterSpacing: ".1rem",
          }}
        >
          Export All to PDF
        </Button>
      </Box>

      <Paper sx={{ bgcolor: "white" }}>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            getRowId={(row) => row?._id || Math.random()}
            loading={loading}
            rows={filteredReports}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              "& .MuiTablePagination-displayedRows": {
                color: "black",
              },
              color: "black",
              [`& .${gridClasses?.row}`]: {
                bgcolor: "white",
              },
              // Style for header cells
              "& .MuiDataGrid-columnHeaders": {
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "black",
                fontWeight: "bold",
              },
              "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
                color: "#FFF",
              },
              "& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root": {
                color: "white",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "white",
              },
            }}
          />
        </Box>
      </Paper>

      <ViewReportModal />
    </div>
  );
};

export default Reports;
